<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import { useCommercialGoalsStore } from 'dashboard/stores/commercialGoals';
import { useCommercialGoalFormat } from 'dashboard/components-next/CommercialGoals/useCommercialGoalFormat';
import GoalProgressBar from 'dashboard/components-next/CommercialGoals/GoalProgressBar.vue';
import {
  formatCurrencyBRL,
  formatDuration,
} from '../Comercial/commercialFormatters';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const goalsStore = useCommercialGoalsStore();
const { formatValue, formatType, formatPeriod } = useCommercialGoalFormat();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getMySummary(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const ROWS = [
  { key: 'revenue', labelKey: 'REVENUE', format: formatCurrencyBRL },
  { key: 'won_count', labelKey: 'WON_COUNT' },
  {
    key: 'average_ticket',
    labelKey: 'AVERAGE_TICKET',
    format: formatCurrencyBRL,
  },
  {
    key: 'conversion_rate',
    labelKey: 'CONVERSION_RATE',
    format: value => `${value}%`,
  },
  {
    key: 'average_purchase_time_seconds',
    labelKey: 'AVERAGE_PURCHASE_TIME',
    format: formatDuration,
  },
  { key: 'created_count', labelKey: 'CREATED_COUNT' },
  { key: 'open_count', labelKey: 'OPEN_COUNT' },
  { key: 'open_value', labelKey: 'OPEN_VALUE', format: formatCurrencyBRL },
  { key: 'lost_count', labelKey: 'LOST_COUNT' },
  { key: 'lost_value', labelKey: 'LOST_VALUE', format: formatCurrencyBRL },
];

const displayValue = row => {
  const raw = data.value?.[row.key];
  if (raw === undefined || raw === null) return '—';
  return row.format ? row.format(raw) : raw;
};

// As individuais do usuário primeiro; as de equipe vêm como contexto.
const myGoals = computed(() => [
  ...goalsStore.myProgress.filter(goal => goal.scopeType === 'user'),
  ...goalsStore.myProgress.filter(goal => goal.scopeType === 'team'),
]);

const scopeLabel = goal =>
  goal.scopeType === 'team'
    ? t('COMMERCIAL_GOALS.SCOPE.TEAM')
    : t('DASHBOARD.COMERCIAL.MY_GOALS.YOU');

const valueSummary = ({ goalType, progress }) =>
  progress.state === 'no_data'
    ? ''
    : t('COMMERCIAL_GOALS.ROW.CURRENT_OF_TARGET', {
        current: formatValue(goalType, progress.currentValue),
        target: formatValue(goalType, progress.targetValue),
      });
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.MEU_ESPACO.SECTIONS.COMMERCIAL') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.MEU_ESPACO.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.MEU_ESPACO.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.MEU_ESPACO.LOADING') }}
    </p>
    <div v-else class="px-3 pb-3 overflow-y-auto overflow-x-auto max-h-80">
      <table class="w-full text-sm">
        <tbody>
          <tr v-for="row in ROWS" :key="row.key" class="border-t border-n-weak">
            <td class="py-1 pr-3 text-n-slate-10">
              {{ t(`DASHBOARD.COMERCIAL.CARDS.${row.labelKey}`) }}
            </td>
            <td class="py-1 pr-3 font-medium text-n-slate-12">
              {{ displayValue(row) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="myGoals.length" class="px-3 pb-3">
      <h3 class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('DASHBOARD.COMERCIAL.MY_GOALS.TITLE') }}
      </h3>
      <ul>
        <li
          v-for="goal in myGoals"
          :key="goal.id"
          class="grid items-center gap-2 py-2 border-t border-n-weak md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.5fr)]"
        >
          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ formatType(goal) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ scopeLabel(goal) }} · {{ formatPeriod(goal) }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11">{{ valueSummary(goal) }}</span>
          <GoalProgressBar
            :percent="goal.progress.progressPercent"
            :state="goal.progress.state"
          />
        </li>
      </ul>
    </div>
  </section>
</template>
